import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@clerk/react';
import type { OnboardingKvState } from '@agentbrush/shared';
import { useOnboardingState } from '../../hooks/use-onboarding-state.js';
import { useOnboardingSse } from '../../hooks/use-onboarding-sse.js';
import { CopyButton } from '../../components/ui/copy-button/copy-button.js';
import { TableOfContents } from '../../components/ui/table-of-contents/table-of-contents.js';
import { pushToast } from '../app/toast-store.js';

type Agent = 'claude-code' | 'cursor' | 'codex-cli' | 'gemini-cli';
type Os = 'mac' | 'win' | 'linux';
type Pkg = 'npm' | 'pnpm' | 'bun';
type StepStatus = 'pending' | 'active' | 'done';

const AGENT_LABEL: Record<Agent, string> = {
  'claude-code': 'Claude Code',
  cursor: 'Cursor',
  'codex-cli': 'Codex',
  'gemini-cli': 'Gemini CLI',
};

const AGENT_ORDER: readonly Agent[] = ['claude-code', 'cursor', 'codex-cli', 'gemini-cli'] as const;

const AGENT_FILE: Record<Agent, Record<Os, string>> = {
  'claude-code': {
    mac: '~/.claude.json',
    win: '%USERPROFILE%\\.claude.json',
    linux: '~/.claude.json',
  },
  cursor: {
    mac: '.cursor/mcp.json',
    win: '.cursor\\mcp.json',
    linux: '.cursor/mcp.json',
  },
  'codex-cli': {
    mac: '.codex/mcp.json',
    win: '.codex\\mcp.json',
    linux: '.codex/mcp.json',
  },
  'gemini-cli': {
    mac: '~/.gemini/settings.json',
    win: '%USERPROFILE%\\.gemini\\settings.json',
    linux: '~/.gemini/settings.json',
  },
};

const AGENT_CONFIG_ROOT_KEY: Record<Agent, 'mcpServers' | 'servers'> = {
  'claude-code': 'mcpServers',
  cursor: 'mcpServers',
  'codex-cli': 'servers',
  'gemini-cli': 'mcpServers',
};

// Package-manager install commands. After install the `agentbrush-mcp` binary
// is on PATH, so steps 02/03/04 don't depend on which manager was used.
const PKG_LABEL: Record<Pkg, string> = { npm: 'npm', pnpm: 'pnpm', bun: 'bun' };
const PKG_ORDER: readonly Pkg[] = ['npm', 'pnpm', 'bun'] as const;

const PKG_INSTALL_CMD: Record<Pkg, string> = {
  npm: 'npm install -g @agentbrush/mcp-server',
  pnpm: 'pnpm add -g @agentbrush/mcp-server',
  bun: 'bun add -g @agentbrush/mcp-server',
};

const CREDENTIALS_PATH: Record<Os, string> = {
  mac: '~/.agentbrush/credentials.json',
  linux: '~/.agentbrush/credentials.json',
  // eslint-disable-next-line no-secrets/no-secrets -- not a secret, a Windows file path constant
  win: '%USERPROFILE%\\.agentbrush\\credentials.json',
};

function shellPrompt(os: Os): string {
  return os === 'win' ? '> ' : '$ ';
}

function detectOs(): Os {
  if (typeof navigator === 'undefined') return 'mac';
  const p = navigator.platform.toLowerCase();
  if (p.includes('win')) return 'win';
  if (p.includes('mac')) return 'mac';
  return 'linux';
}

// JSON snippet used as the manual-edit fallback for agents without a `mcp add`
// CLI. Since `agentbrush-mcp` is globally installed in step 01, the snippet
// only needs the binary name — no package-manager args.
function jsonSnippetFor(agent: Agent): string {
  const key = AGENT_CONFIG_ROOT_KEY[agent];
  return JSON.stringify({ [key]: { agentbrush: { command: 'agentbrush-mcp' } } }, null, 2);
}

// Per-agent registration: Claude Code ships a `claude mcp add` CLI; the
// others ask the user to merge a JSON snippet into the agent's config file.
type Registration =
  | { kind: 'cli'; command: string; note?: string }
  | { kind: 'json'; filePath: string; snippet: string };

function registrationFor(agent: Agent, os: Os): Registration {
  if (agent === 'claude-code') {
    return {
      kind: 'cli',
      command: 'claude mcp add agentbrush --scope user -- agentbrush-mcp',
      note: '--scope user installs the server for every Claude Code session on this machine. Drop the flag to scope it to the current project.',
    };
  }
  return {
    kind: 'json',
    filePath: AGENT_FILE[agent][os],
    snippet: jsonSnippetFor(agent),
  };
}

/**
 * Four visible steps; only the last three carry live status from the
 * onboarding SSE stream. The first step (Install) is UI-only — we can't
 * reliably detect a local `npm install -g` without an explicit event.
 *
 *   01 Install              — UI only
 *   02 Authenticate         — done when `mcpAuthenticated`
 *   03 Connect to <Agent>   — done when `mcpInstalled` (the MCP reaches the
 *                             backend through the agent at least once)
 *   04 Generate             — done when `firstGenerationComplete`
 *
 * Status is session-scoped: a flag only counts as "done" if it transitioned
 * to true during this visit. A user returning to the page after a prior
 * completed run sees every step uncleared again so they can install the MCP
 * on a new machine without the page lying about their progress.
 *
 * Returns `null` for unauthenticated visitors so steps render with no status
 * decoration — the page works as a public install reference, not a stripped
 * progress tracker.
 */
function deriveStepStatuses(
  state: OnboardingKvState | undefined,
  baseline: OnboardingKvState | null,
  signedIn: boolean,
): [StepStatus, StepStatus, StepStatus] | null {
  if (!signedIn) return null;
  if (!state) return ['active', 'active', 'active'];
  // A flag is "earned this session" only when it's true now AND was false at
  // baseline. Every step starts active so the user can act on any of them in
  // any order; a step only flips to done when its own validating event fires.
  const mcpAuthenticated = state.mcpAuthenticated && !baseline?.mcpAuthenticated;
  const mcpInstalled = state.mcpInstalled && !baseline?.mcpInstalled;
  const firstGenerationComplete =
    state.firstGenerationComplete && !baseline?.firstGenerationComplete;
  return [
    mcpAuthenticated ? 'done' : 'active',
    mcpInstalled ? 'done' : 'active',
    firstGenerationComplete ? 'done' : 'active',
  ];
}

export function GettingStartedPage() {
  const [agent, setAgent] = useState<Agent>('claude-code');
  const [os, setOs] = useState<Os>(() => detectOs());
  const [pkg, setPkg] = useState<Pkg>('npm');
  const { userId, isSignedIn } = useAuth();
  const { data: state } = useOnboardingState();
  useOnboardingSse(userId ?? '');

  // Pre-fill the agent chip from server-side cliTool once it lands.
  const cliToolApplied = useRef(false);
  useEffect(() => {
    if (cliToolApplied.current) return;
    if (state?.cliTool && state.cliTool in AGENT_LABEL) {
      setAgent(state.cliTool as Agent);
      cliToolApplied.current = true;
    }
  }, [state?.cliTool]);

  // Snapshot the KV state on its first arrival. Everything downstream — step
  // status, the "Last generation" preview link, the celebration toast — keys
  // off transitions away from this baseline, so a returning user who already
  // completed onboarding sees the page reset and can install on a new
  // machine without the steps lying about their progress.
  const stateBaselineRef = useRef<OnboardingKvState | null>(null);
  if (state && stateBaselineRef.current === null) {
    stateBaselineRef.current = state;
  }
  const baseline = stateBaselineRef.current;

  // One-shot celebration toast on the off → on transition of
  // `firstGenerationComplete` relative to the captured baseline.
  const celebratedRef = useRef(false);
  useEffect(() => {
    if (state == null || baseline == null) return;
    if (celebratedRef.current) return;
    if (state.firstGenerationComplete && !baseline.firstGenerationComplete) {
      celebratedRef.current = true;
      pushToast({
        tone: 'success',
        title: "You're all set",
        description: 'Your first generation succeeded — AgentBrush is wired in.',
      });
    }
  }, [state, baseline]);

  const stepStatuses = useMemo(
    () => deriveStepStatuses(state, baseline, !!isSignedIn),
    [state, baseline, isSignedIn],
  );

  // 1/2/3 keyboard shortcut to switch platforms (global control).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === '1') setOs('mac');
      else if (e.key === '2') setOs('win');
      else if (e.key === '3') setOs('linux');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const installCmd = PKG_INSTALL_CMD[pkg];
  const registration = registrationFor(agent, os);
  const credPath = CREDENTIALS_PATH[os];

  return (
    <div className="flex w-full justify-between gap-12 px-6 pb-32 pt-16 standard:px-10">
      <div className="min-w-0 flex-1 wide:max-w-[960px]">
        <p className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Getting started
        </p>
        <h1 className="mt-4 max-w-[20ch] font-sans text-[clamp(36px,6vw,56px)] font-light leading-[1.05] tracking-[-0.03em] text-primary">
          Connect AgentBrush to your coding agent.
        </h1>
        <p className="mb-10 mt-4 max-w-[60ch] text-[16px] leading-[1.6] text-secondary">
          AgentBrush ships as an MCP server your agent talks to over stdio. Three terminal commands
          — install, log in, register — and your agent can generate brand-consistent images from
          inside the editor.
        </p>

        <PlatformBar os={os} setOs={setOs} />

        <Step n="01" title="Install AgentBrush" status={isSignedIn ? 'active' : undefined}>
          <p>
            Install the MCP server globally so the <Inline>agentbrush-mcp</Inline> binary lands on
            your PATH. Your agent spawns it on demand from there.
          </p>
          <PkgTabs pkg={pkg} setPkg={setPkg} />
          <CodeLine copy={installCmd}>
            <span className="select-none text-muted">{shellPrompt(os)}</span>
            {installCmd}
          </CodeLine>
          <p className="text-muted">
            One-time setup. Re-run with <Inline>@latest</Inline> appended to pick up new versions.
          </p>
        </Step>

        <Step
          n="02"
          title="Authenticate"
          status={stepStatuses?.[0]}
          waitingHint="Waiting for device-code approval…"
        >
          <p>
            Run the init command in your terminal. It opens your browser with an 8-character device
            code already filled in — click <b className="text-primary">Approve</b> and the MCP saves
            your credentials. No agent setup needed for this step.
          </p>
          <CodeLine copy="agentbrush-mcp init">
            <span className="select-none text-muted">{shellPrompt(os)}</span>
            agentbrush-mcp init
          </CodeLine>
          <p>
            Credentials land in <Inline>{credPath}</Inline> — you never paste an API key by hand.
            Your agent can also kick off this flow from inside the editor, but running it in the
            terminal is cleaner because the browser handoff doesn't have to bounce through the
            agent.
          </p>
        </Step>

        <Step
          n="03"
          title={`Connect to ${AGENT_LABEL[agent]}`}
          status={stepStatuses?.[1]}
          waitingHint="Waiting for your agent to call AgentBrush…"
        >
          <p>
            Pick your agent — the registration step below changes based on this choice. AgentBrush
            works with Claude Code, Cursor, Codex, and Gemini CLI.
          </p>
          <AgentTabs agent={agent} setAgent={setAgent} />
          {registration.kind === 'cli' ? (
            <>
              <p className="mt-4">
                One command — {AGENT_LABEL[agent]}'s own CLI wires the MCP server into your config:
              </p>
              <CodeLine copy={registration.command}>
                <span className="select-none text-muted">{shellPrompt(os)}</span>
                {registration.command}
              </CodeLine>
              {registration.note && <p className="text-muted">{registration.note}</p>}
            </>
          ) : (
            <>
              <p className="mt-4">
                {AGENT_LABEL[agent]} reads its MCP servers from{' '}
                <Inline>{registration.filePath}</Inline>. Merge the snippet below into that file —
                create it if it doesn't exist — then restart {AGENT_LABEL[agent]}.
              </p>
              <CodeBlock copy={registration.snippet}>{registration.snippet}</CodeBlock>
              <p className="text-muted">
                The <Inline>command</Inline> field points at the binary you installed in step 01.{' '}
                {AGENT_LABEL[agent]} spawns it over stdio whenever it needs the AgentBrush tools.
              </p>
            </>
          )}
        </Step>

        <Step
          n="04"
          title="Generate"
          status={stepStatuses?.[2]}
          waitingHint="Waiting for your first generation…"
          last
        >
          <p>
            Just describe what you want — your agent will pick the right preset and call{' '}
            <Inline>agentbrush_generate</Inline> automatically.
          </p>
          <CodeLine copy="Draw a flat hero illustration for the pricing page, lime accent">
            <span className="select-none text-muted">{'> '}</span>
            Draw a flat hero illustration for the pricing page, lime accent
          </CodeLine>
          {state?.firstGenerationPreview && !baseline?.firstGenerationComplete && (
            <p className="mt-3 text-muted">
              <span className="font-mono text-[11px] uppercase tracking-[0.12em]">
                Last generation
              </span>{' '}
              <a
                className="text-accent-blue hover:underline"
                href={state.firstGenerationPreview}
                target="_blank"
                rel="noreferrer"
              >
                open preview ↗
              </a>
            </p>
          )}
        </Step>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <Link
            to="/docs"
            className="inline-flex items-center gap-2 rounded-md bg-accent-lime px-4 py-2.5 text-[13px] font-semibold text-canvas transition-[filter] hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            Read the full docs →
          </Link>
          {isSignedIn ? (
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-1 py-2.5 text-[13px] text-secondary transition-colors hover:text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
            >
              Open dashboard →
            </Link>
          ) : (
            <Link
              to="/sign-up"
              className="inline-flex items-center gap-2 px-1 py-2.5 text-[13px] text-secondary transition-colors hover:text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
            >
              Create an account →
            </Link>
          )}
        </div>

        <footer className="mt-20 flex items-center justify-between border-t border-white/[0.06] py-6 font-mono text-[11px] text-muted">
          <span>AgentBrush · v0.4</span>
          <nav className="flex flex-wrap gap-x-5 gap-y-2">
            <Link className="text-muted hover:text-secondary" to="/impressum">
              Legal Notice
            </Link>
            <Link className="text-muted hover:text-secondary" to="/privacy">
              Privacy
            </Link>
            <Link className="text-muted hover:text-secondary" to="/terms">
              Terms
            </Link>
            <Link className="text-muted hover:text-secondary" to="/cookie-policy">
              Cookies
            </Link>
            <Link className="text-muted hover:text-secondary" to="/contact">
              Contact
            </Link>
            <Link className="text-muted hover:text-secondary" to="/docs">
              Docs
            </Link>
            <Link className="text-muted hover:text-secondary" to="/changelog">
              Changelog
            </Link>
          </nav>
        </footer>
      </div>
      <TableOfContents
        items={[
          { id: 'step-01', label: '01 · Install' },
          { id: 'step-02', label: '02 · Authenticate' },
          { id: 'step-03', label: '03 · Connect' },
          { id: 'step-04', label: '04 · Generate' },
        ]}
      />
    </div>
  );
}

// ── Step (status-aware, status?=undefined → neutral) ─────────────────────────

function Step({
  n,
  title,
  status,
  waitingHint,
  last = false,
  children,
}: {
  n: string;
  title: React.ReactNode;
  status?: StepStatus | undefined;
  waitingHint?: string | undefined;
  last?: boolean;
  children: React.ReactNode;
}) {
  const isDone = status === 'done';
  const isActive = status === 'active';

  return (
    <div
      id={`step-${n}`}
      className={[
        'scroll-mt-24 grid grid-cols-[60px_1fr] gap-6 border-t border-white/[0.06] py-8 transition-opacity duration-500',
        last ? 'border-b' : '',
        isDone ? 'opacity-55' : '',
      ].join(' ')}
      data-step-status={status ?? 'neutral'}
    >
      <span className="pt-1 font-mono text-[13px] tracking-[0.04em]">
        {isDone ? (
          <CheckIcon />
        ) : (
          <span className={isActive ? 'text-accent-lime' : 'text-muted'}>{n}</span>
        )}
      </span>
      <div>
        <h3 className="m-0 mb-2 flex flex-wrap items-baseline gap-3 font-sans text-[16px] font-medium leading-[1.3] tracking-[-0.01em] text-primary">
          <span className={isDone ? 'line-through decoration-white/[0.18] decoration-1' : ''}>
            {title}
          </span>
          {isActive && waitingHint && (
            <span className="inline-flex items-center gap-1.5 font-mono text-[11px] font-normal text-muted">
              <span
                className="block h-1.5 w-1.5 rounded-full bg-accent-lime motion-safe:animate-pulse"
                aria-hidden="true"
              />
              {waitingHint}
            </span>
          )}
        </h3>
        <div className="text-[14px] leading-[1.5] text-secondary [&>p:not(:first-child)]:mt-3 [&>p]:m-0 [&>p]:max-w-[60ch]">
          {children}
        </div>
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4 text-accent-lime" aria-hidden="true">
      <path
        d="M3.5 8.5L6.5 11.5L12.5 5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Global platform bar (sits above step 01, drives file paths + shell) ─────

function PlatformBar({ os, setOs }: { os: Os; setOs: (o: Os) => void }) {
  return (
    <div
      role="tablist"
      aria-label="Platform"
      className="mb-2 flex items-stretch gap-0 border-y border-white/[0.06]"
    >
      <span className="self-center pr-4 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
        Platform
      </span>
      <PTab active={os === 'mac'} onClick={() => setOs('mac')} icon={<MacIcon />}>
        macOS
      </PTab>
      <PTab active={os === 'win'} onClick={() => setOs('win')} icon={<WinIcon />}>
        Windows
      </PTab>
      <PTab active={os === 'linux'} onClick={() => setOs('linux')} icon={<LinuxIcon />}>
        Linux
      </PTab>
      <span className="flex-1" />
      <span className="hidden self-center pr-1 font-mono text-[11px] tracking-[0.04em] text-muted standard:inline">
        switch with <Kbd>1</Kbd>
        <Kbd>2</Kbd>
        <Kbd>3</Kbd>
      </span>
    </div>
  );
}

function PTab({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        'relative -mb-px inline-flex items-center gap-2 border-b border-transparent bg-transparent px-4 py-2.5 font-sans text-[13px] font-medium transition-colors',
        'outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-400',
        active
          ? 'text-primary [border-bottom-color:var(--color-accent-lime)]'
          : 'text-muted hover:text-secondary',
      ].join(' ')}
    >
      <span className="inline-flex h-3.5 w-3.5 items-center justify-center">{icon}</span>
      {children}
    </button>
  );
}

// ── Package-manager tabs (lives inside step 01, drives the install command) ─

function PkgTabs({ pkg, setPkg }: { pkg: Pkg; setPkg: (p: Pkg) => void }) {
  return (
    <div
      role="tablist"
      aria-label="Package manager"
      className="mt-2 mb-1 flex items-stretch gap-0 border-b border-white/[0.06]"
    >
      {PKG_ORDER.map((p) => (
        <PkgTab key={p} active={pkg === p} onClick={() => setPkg(p)}>
          {PKG_LABEL[p]}
        </PkgTab>
      ))}
    </div>
  );
}

function PkgTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        'relative -mb-px inline-flex items-center bg-transparent px-3 py-2 font-mono text-[12px] font-medium uppercase tracking-[0.06em] transition-colors',
        'border-b border-transparent outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-400',
        active
          ? 'text-primary [border-bottom-color:var(--color-accent-lime)]'
          : 'text-muted hover:text-secondary',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="mx-0.5 rounded-[3px] border border-border-bright px-1.5 py-0.5 font-mono text-[10px] font-medium text-secondary">
      {children}
    </kbd>
  );
}

function MacIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5" aria-hidden="true">
      <path d="M11.4 8.5c0-1.4 1.2-2.1 1.2-2.1-.7-1-1.7-1.1-2.1-1.1-.9-.1-1.7.5-2.2.5s-1.2-.5-1.9-.5c-1 0-1.9.6-2.4 1.5C2.9 8.7 3.7 11.7 4.7 13.3c.5.8 1.1 1.7 1.9 1.7s1-.5 2-.5 1.2.5 2 .5 1.4-.8 1.9-1.7c.6-.9.8-1.8.9-1.9-.1 0-1.9-.7-1.9-2.9zM10 4.4c.4-.5.7-1.2.6-2-.6 0-1.4.4-1.8.9-.4.4-.8 1.2-.7 1.9.7.1 1.4-.3 1.9-.8z" />
    </svg>
  );
}

function WinIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5" aria-hidden="true">
      <path d="M0 2.5 6.5 1.6v6.4H0V2.5zM7.3 1.5 16 .3v8H7.3V1.5zM0 8.8h6.5v6.4L0 14.3V8.8zm7.3 0H16v8L7.3 15.5V8.8z" />
    </svg>
  );
}

function LinuxIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path d="M5 3.5C5 2 6 1 8 1s3 1 3 2.5c0 1.7-1 3-1 4.5 0 1 .5 1.5 1.5 2.5s2 2 2 3-1 1.5-2 1.5h-7c-1 0-2-.5-2-1.5s1-2 2-3 1.5-1.5 1.5-2.5c0-1.5-1-2.8-1-4.5z" />
      <circle cx="6.8" cy="3.5" r=".5" fill="currentColor" />
      <circle cx="9.2" cy="3.5" r=".5" fill="currentColor" />
    </svg>
  );
}

// ── Code surfaces ────────────────────────────────────────────────────────────

function CodeLine({ children, copy }: { children: React.ReactNode; copy: string }) {
  return (
    <div className="mt-3 flex items-start justify-between gap-4 rounded-md border border-white/[0.06] bg-[#0c0c0c] px-4 py-3.5">
      <div className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap font-mono text-[13.5px] leading-[1.6] text-primary">
        {children}
      </div>
      <CopyButton text={copy} variant="ghost" icon="none" />
    </div>
  );
}

function CodeBlock({ children, copy }: { children: string; copy: string }) {
  return (
    <div className="mt-3 flex items-start gap-4 rounded-md border border-white/[0.06] bg-[#0c0c0c] px-4 py-3.5">
      <pre className="m-0 min-w-0 flex-1 overflow-x-auto whitespace-pre font-mono text-[13px] leading-[1.65] text-primary">
        {children}
      </pre>
      <CopyButton text={copy} variant="ghost" icon="none" />
    </div>
  );
}

function Inline({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded-[3px] border border-white/[0.06] bg-[#0c0c0c] px-1.5 py-0.5 font-mono text-[12.5px] text-accent-cyan">
      {children}
    </code>
  );
}

// ── Agent tabs (step 03) ─────────────────────────────────────────────────────
// Same underline-tab style as the platform bar and the package-manager tabs in
// step 01, so every selector on the page reads identically.

function AgentTabs({ agent, setAgent }: { agent: Agent; setAgent: (a: Agent) => void }) {
  return (
    <div
      role="tablist"
      aria-label="Coding agent"
      className="mt-3 mb-1 flex flex-wrap items-stretch gap-0 border-b border-white/[0.06]"
    >
      {AGENT_ORDER.map((a) => (
        <AgentTab key={a} active={agent === a} onClick={() => setAgent(a)}>
          {AGENT_LABEL[a]}
        </AgentTab>
      ))}
    </div>
  );
}

function AgentTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        'relative -mb-px inline-flex items-center bg-transparent px-3 py-2 font-mono text-[12px] font-medium uppercase tracking-[0.06em] transition-colors',
        'border-b border-transparent outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-400',
        active
          ? 'text-primary [border-bottom-color:var(--color-accent-lime)]'
          : 'text-muted hover:text-secondary',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
